import { cloneDeep } from "lodash";
import proj4 from "proj4";
import { ProjectionsRegistrar } from "./ProjectionRegistrar";

ProjectionsRegistrar.RegisterEPSG27700();
ProjectionsRegistrar.RegisterEastingNorthing();

export class GeometryProjector {
    public static projCoordinatesToWGS84(coordinates: GeoJSON.Position, projFrom = "EPSG:27700") {
        return proj4(projFrom, "WGS84", coordinates);
    }
    public static projectTo27700(geometry: GeoJSON.FeatureCollection): GeoJSON.FeatureCollection {
        const result = cloneDeep(geometry);
        for (const feature of result.features) {
            const geometry = feature.geometry as GeoJSON.Geometry;
            if (!geometry) {
                continue;
            }
            this.projGeometryTo27700(geometry);
        }
        return result;
    }

    private static projCoordTo27700(coord: GeoJSON.Position): GeoJSON.Position {
        const projected = proj4("WGS84", "EPSG:27700", coord);
        return [Math.round(projected[0] * 100) / 100, Math.round(projected[1] * 100) / 100];
    }

    private static projGeometryTo27700(geometry: GeoJSON.Geometry): void {
        if (geometry.type === "Point") {
            geometry.coordinates = this.projCoordTo27700(geometry.coordinates);
        } else if (geometry.type === "MultiPoint") {
            for (let i = 0; i < geometry.coordinates.length; i++) {
                geometry.coordinates[i] = this.projCoordTo27700(geometry.coordinates[i]);
            }
        } else if (geometry.type === "LineString") {
            for (let i = 0; i < geometry.coordinates.length; i++) {
                geometry.coordinates[i] = this.projCoordTo27700(geometry.coordinates[i]);
            }
        } else if (geometry.type === "MultiLineString") {
            for (const line of geometry.coordinates) {
                for (let i = 0; i < line.length; i++) {
                    line[i] = this.projCoordTo27700(line[i]);
                }
            }
        } else if (geometry.type === "Polygon") {
            for (const ring of geometry.coordinates) {
                for (let i = 0; i < ring.length; i++) {
                    ring[i] = this.projCoordTo27700(ring[i]);
                }
            }
        } else if (geometry.type === "MultiPolygon") {
            for (const polygon of geometry.coordinates) {
                for (const ring of polygon) {
                    for (let i = 0; i < ring.length; i++) {
                        ring[i] = this.projCoordTo27700(ring[i]);
                    }
                }
            }
        } else if (geometry.type === "GeometryCollection") {
            for (const geom of (geometry as GeoJSON.GeometryCollection).geometries) {
                this.projGeometryTo27700(geom);
            }
        } else {
            throw new Error("Geometry type not supported yet: " + (geometry as any).type);
        }
    }

    public static eastingNorthingToWGS(easting: number, northing: number): [number, number] {
        const neighbourCoordinates = { coordinates: [easting, northing], type: "Point" } as GeoJSON.Point;
        const geo = this.projectToWGS(neighbourCoordinates, ProjectionsRegistrar.EastingNorthingName) as GeoJSON.Point;
        const coordinates = geo.coordinates.reverse();
        return [coordinates[0], coordinates[1]];
    }
    public static projectToWGS(geometry: GeoJSON.Geometry | GeoJSON.FeatureCollection | GeoJSON.Feature, projFrom = "EPSG:27700"): GeoJSON.Geometry | GeoJSON.FeatureCollection {
        const result = cloneDeep(geometry);
        const polygon = result as GeoJSON.Polygon;
        if (polygon.coordinates) {
            this.projGeometryToWGS84(polygon, projFrom);
            return result as GeoJSON.Geometry | GeoJSON.FeatureCollection;
        }
        if ("type" in result && (result as any).type == "Feature") {
            const feature = result as unknown as GeoJSON.Feature;
            this.projGeometryToWGS84(feature.geometry, projFrom);
            return feature.geometry as GeoJSON.Geometry;
        } else if ("type" in result && (result as any).type === "GeometryCollection") {
            const geometryCollection = result as unknown as GeoJSON.GeometryCollection;
            for (let i = 0; i < geometryCollection.geometries.length; i++) {
                const geom = geometryCollection.geometries[i];
                this.projGeometryToWGS84(geom, projFrom);
            }
            return geometryCollection;
        } else {
            const featureCollection = result as GeoJSON.FeatureCollection | undefined;
            if (!featureCollection || !Array.isArray(featureCollection.features)) {
                this.projGeometryToWGS84(result as GeoJSON.Geometry, projFrom);
                return result as GeoJSON.Geometry | GeoJSON.FeatureCollection;
            }
            for (let i = 0; i < featureCollection.features.length; i++) {
                const feature = featureCollection.features[i];
                const geom = feature.geometry;
                this.projGeometryToWGS84(geom, projFrom);
            }
            return result as GeoJSON.Geometry | GeoJSON.FeatureCollection;
        }
    }
    private static projGeometryToWGS84(geometry: GeoJSON.Geometry, projFrom: string) {
        if (geometry.type === "MultiPolygon") {
            const polygons = geometry.coordinates;
            for (let p = 0; p < polygons.length; p++) {
                const polygon = polygons[p];
                for (let r = 0; r < polygon.length; r++) {
                    const ring = polygon[r];
                    for (let c = 0; c < ring.length; c++) {
                        ring[c] = proj4(projFrom, "WGS84", ring[c]);
                    }
                }
            }
        } else if (geometry.type === "MultiLineString") {
            const lines = geometry.coordinates;
            for (let l = 0; l < lines.length; l++) {
                const line = lines[l];
                for (let c = 0; c < line.length; c++) {
                    line[c] = proj4(projFrom, "WGS84", line[c]);
                }
            }
        } else if (geometry.type === "LineString") {
            const line = geometry.coordinates;
            for (let c = 0; c < line.length; c++) {
                line[c] = proj4(projFrom, "WGS84", line[c]);
            }
        } else if (geometry.type === "Polygon") {
            const polygon = geometry.coordinates;
            for (let r = 0; r < polygon.length; r++) {
                const ring = polygon[r];
                for (let c = 0; c < ring.length; c++) {
                    ring[c] = proj4(projFrom, "WGS84", ring[c]);
                }
            }
        } else if (geometry.type === "Point") {
            const coords = proj4(projFrom, "WGS84", geometry.coordinates);
            geometry.coordinates = coords;
        } else if (geometry.type === "MultiPoint") {
            const points = geometry.coordinates;
            for (let p = 0; p < points.length; p++) {
                points[p] = proj4(projFrom, "WGS84", points[p]);
            }
        } else if (geometry.type === "GeometryCollection") {
            for (const geom of (geometry as GeoJSON.GeometryCollection).geometries) {
                this.projGeometryToWGS84(geom, projFrom);
            }
        } else {
            throw new Error("Unsupported geometry type: " + (geometry as any).type);
        }
    }
}
